import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Calendar,
  Users,
  Briefcase,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Play,
  Square,
  Loader2,
  DollarSign,
  TrendingUp,
  Target,
  BarChart3,
  Phone,
  MessageSquare,
  Check,
  X,
} from 'lucide-react';
import { formatDate, formatTime, getStatusColor, getStatusLabel, formatDuration } from '../lib/utils';
import { toast } from 'sonner';

// Format currency in AUD
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(value);
};

// Format period display label
const getPeriodLabel = (period) => {
  switch (period) {
    case 'week': return 'This Week';
    case 'month': return 'This Month';
    case 'year': return 'This Year';
    default: return period;
  }
};

const DashboardPage = () => {
  const { api, user, isManager, isStaff, isDirector } = useAuth();
  const [stats, setStats] = useState(null);
  const [todaysBookings, setTodaysBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Revenue states
  const [revenueStats, setRevenueStats] = useState(null);
  const [revenuePeriod, setRevenuePeriod] = useState('month');
  const [revenueLoading, setRevenueLoading] = useState(false);
  
  // Revenue breakdown states
  const [revenueBreakdown, setRevenueBreakdown] = useState(null);
  const [breakdownMetric, setBreakdownMetric] = useState('completed');
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  
  // Upsell notes states
  const [upsellNotes, setUpsellNotes] = useState([]);
  const [upsellLoading, setUpsellLoading] = useState(false);
  
  // Check if user can view financial data
  const canViewFinancials = isDirector || user?.permissions?.view_financials;

  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Fetch upsell notes for directors
  useEffect(() => {
    if (isDirector) {
      fetchUpsellNotes();
    }
  }, [isDirector]);
  
  // Fetch revenue stats when period changes or on initial load
  useEffect(() => {
    if (canViewFinancials) {
      fetchRevenueStats();
      fetchRevenueBreakdown();
    }
  }, [revenuePeriod, canViewFinancials]);
  
  // Fetch breakdown when metric changes
  useEffect(() => {
    if (canViewFinancials) {
      fetchRevenueBreakdown();
    }
  }, [breakdownMetric]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, todayRes] = await Promise.all([
        api().get('/admin/dashboard-stats'),
        api().get('/bookings/today'),
      ]);
      setStats(statsRes.data);
      setTodaysBookings(todayRes.data.bookings);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRevenueStats = async () => {
    if (!canViewFinancials) return;
    
    setRevenueLoading(true);
    try {
      const res = await api().get(`/admin/revenue-stats?period=${revenuePeriod}`);
      setRevenueStats(res.data);
    } catch (error) {
      console.error('Failed to fetch revenue stats:', error);
      // Don't toast on permission errors - just silently fail
      if (error.response?.status !== 403) {
        toast.error('Failed to load revenue data');
      }
    } finally {
      setRevenueLoading(false);
    }
  };
  
  const fetchRevenueBreakdown = async () => {
    if (!canViewFinancials) return;
    
    setBreakdownLoading(true);
    try {
      const res = await api().get(`/admin/revenue-by-category?period=${revenuePeriod}&metric=${breakdownMetric}`);
      setRevenueBreakdown(res.data);
    } catch (error) {
      console.error('Failed to fetch revenue breakdown:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load revenue breakdown');
      }
    } finally {
      setBreakdownLoading(false);
    }
  };
  
  const fetchUpsellNotes = async () => {
    if (!isDirector) return;
    
    setUpsellLoading(true);
    try {
      const res = await api().get('/admin/upsell-notes?status=pending');
      setUpsellNotes(res.data.notes || []);
    } catch (error) {
      console.error('Failed to fetch upsell notes:', error);
    } finally {
      setUpsellLoading(false);
    }
  };
  
  const handleUpsellAction = async (noteId, status) => {
    try {
      await api().patch(`/admin/upsell-notes/${noteId}`, { status });
      toast.success(status === 'contacted' ? 'Marked as contacted' : 'Note dismissed');
      fetchUpsellNotes();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleAccept = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await api().post(`/bookings/${bookingId}/accept`);
      toast.success('Job accepted!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArrive = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await api().post(`/bookings/${bookingId}/arrive`);
      toast.success('Arrival recorded successfully!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record arrival');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDepart = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await api().post(`/bookings/${bookingId}/depart`);
      toast.success('Job completed! Great work!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record departure');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">
          {greeting()}, {user?.full_name?.split(' ')[0]}
        </h1>
        <p className="text-zinc-500 mt-1">Here's what's happening today</p>
      </div>

      {/* Stats Grid */}
      {isManager && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-zinc-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Today's Jobs</p>
                  <p className="text-2xl font-semibold text-zinc-900">{stats?.todays_bookings || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">In Progress</p>
                  <p className="text-2xl font-semibold text-zinc-900">{stats?.in_progress || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Completed</p>
                  <p className="text-2xl font-semibold text-zinc-900">{stats?.completed_today || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Pending Accept</p>
                  <p className="text-2xl font-semibold text-zinc-900">{stats?.pending_acceptance || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Upsell Opportunities - Only for Directors */}
      {isDirector && upsellNotes.length > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/30" data-testid="upsell-notes-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                <CardTitle className="text-lg font-semibold text-emerald-900">
                  Upsell Opportunities
                </CardTitle>
                <Badge className="bg-emerald-500 text-white">{upsellNotes.length}</Badge>
              </div>
            </div>
            <p className="text-sm text-emerald-700">Staff have flagged these clients for additional services</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {upsellLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
              </div>
            ) : (
              upsellNotes.slice(0, 5).map((note) => (
                <div 
                  key={note.id} 
                  className="p-4 bg-white rounded-lg border border-emerald-100 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-zinc-900">{note.client_name}</span>
                        {note.client_phone && (
                          <a 
                            href={`tel:${note.client_phone}`}
                            className="text-emerald-600 hover:text-emerald-700"
                          >
                            <Phone className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                      <p className="text-sm text-zinc-600 mb-2">{note.note}</p>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        <span>From: {note.staff_name}</span>
                        <span>•</span>
                        <span>{new Date(note.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => handleUpsellAction(note.id, 'contacted')}
                        data-testid={`upsell-contact-${note.id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Contacted
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-zinc-400 hover:text-zinc-600"
                        onClick={() => handleUpsellAction(note.id, 'dismissed')}
                        data-testid={`upsell-dismiss-${note.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Revenue Tiles - Only visible to users with view_financials permission */}
      {canViewFinancials && (
        <Card className="border-zinc-100" data-testid="revenue-tiles-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-lg font-semibold text-zinc-900">Revenue Overview</CardTitle>
            </div>
            <Select value={revenuePeriod} onValueChange={setRevenuePeriod}>
              <SelectTrigger className="w-[140px]" data-testid="revenue-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {revenueLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
              </div>
            ) : revenueStats ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Forecast Tile */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100" data-testid="forecast-tile">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-blue-700">Booked Value</span>
                    <Target className="h-5 w-5 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-blue-900" data-testid="forecast-value">
                    {formatCurrency(revenueStats.forecast?.value)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {revenueStats.forecast?.jobs_count || 0} scheduled jobs ({getPeriodLabel(revenuePeriod)})
                  </p>
                </div>

                {/* Actual Tile */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100" data-testid="actual-tile">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-emerald-700">Completed Value</span>
                    <DollarSign className="h-5 w-5 text-emerald-500" />
                  </div>
                  <p className="text-2xl font-bold text-emerald-900" data-testid="actual-value">
                    {formatCurrency(revenueStats.actual?.value)}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">
                    {revenueStats.actual?.jobs_count || 0} completed jobs ({getPeriodLabel(revenuePeriod)})
                  </p>
                </div>

                {/* Revenue to Date Tile */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100" data-testid="revenue-to-date-tile">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-amber-700">Revenue to Date</span>
                    <TrendingUp className="h-5 w-5 text-amber-500" />
                  </div>
                  <p className="text-2xl font-bold text-amber-900" data-testid="revenue-to-date-value">
                    {formatCurrency(revenueStats.revenue_to_date?.value)}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    {revenueStats.revenue_to_date?.jobs_count || 0} total completed jobs
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <p>Unable to load revenue data</p>
              </div>
            )}
            
            {/* Period info */}
            {revenueStats && (
              <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500">
                <span>
                  Period: {revenueStats.period_start} to {revenueStats.period_end} (AEST)
                </span>
                <span className="text-zinc-400">
                  Week starts Monday
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Revenue Breakdown by Category - Only visible to users with view_financials permission */}
      {canViewFinancials && (
        <Card className="border-zinc-100" data-testid="revenue-breakdown-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-lg font-semibold text-zinc-900">Revenue by Category</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {/* Metric Toggle */}
              <Select value={breakdownMetric} onValueChange={setBreakdownMetric}>
                <SelectTrigger className="w-[140px]" data-testid="breakdown-metric-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="forecast">Forecast</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {breakdownLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
              </div>
            ) : revenueBreakdown ? (
              <div className="space-y-4">
                {/* Total summary */}
                <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                  <span className="text-sm font-medium text-zinc-700">
                    Total {breakdownMetric === 'completed' ? 'Completed' : 'Forecast'} Revenue
                  </span>
                  <div className="text-right">
                    <span className="text-lg font-bold text-zinc-900">{formatCurrency(revenueBreakdown.total_revenue)}</span>
                    <span className="text-xs text-zinc-500 ml-2">({revenueBreakdown.total_jobs} jobs)</span>
                  </div>
                </div>

                {/* Category bars */}
                <div className="space-y-3">
                  {revenueBreakdown.breakdown.map((category) => {
                    const maxRevenue = Math.max(...revenueBreakdown.breakdown.map(c => c.revenue), 1);
                    const barWidth = category.revenue > 0 ? Math.max((category.revenue / maxRevenue) * 100, 3) : 0;
                    
                    return (
                      <div key={category.category_id || 'uncategorized'} className="space-y-1" data-testid={`category-bar-${category.category_id || 'uncategorized'}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-zinc-700">{category.category_name}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-zinc-500">{category.jobs_count} jobs</span>
                            <span className="font-semibold text-zinc-900 tabular-nums">{formatCurrency(category.revenue)}</span>
                            <span className="text-xs text-zinc-400 w-12 text-right">{category.percentage}%</span>
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-300 ${
                              category.revenue > 0 
                                ? breakdownMetric === 'completed' 
                                  ? 'bg-emerald-500' 
                                  : 'bg-blue-500'
                                : 'bg-zinc-200'
                            }`}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Period info */}
                <div className="pt-3 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500">
                  <span>
                    {breakdownMetric === 'completed' ? 'Based on completed_at' : 'Based on scheduled_date'} • {revenueBreakdown.period_start} to {revenueBreakdown.period_end} (AEST)
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <p>Unable to load revenue breakdown</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Today's Jobs */}
      <Card className="border-zinc-100">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold text-zinc-900">Today's Jobs</CardTitle>
          <Link to="/bookings">
            <Button variant="ghost" size="sm" className="text-zinc-600">
              View all <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {todaysBookings.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-16 w-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-medium text-zinc-900 mb-1">No jobs today</h3>
              <p className="text-zinc-500">Enjoy your day off!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {todaysBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-colors"
                  data-testid={`booking-card-${booking.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={getStatusColor(booking.status)}>
                        {getStatusLabel(booking.status)}
                      </Badge>
                      {booking.time_window_start && (
                        <span className="text-sm text-zinc-500">
                          {booking.time_window_start} - {booking.time_window_end}
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-zinc-900 truncate">{booking.client_name}</h4>
                    <p className="text-sm text-zinc-500 truncate">{booking.service_address}</p>
                    {booking.assigned_staff_name && isManager && (
                      <p className="text-sm text-zinc-400 mt-1">
                        Assigned to: {booking.assigned_staff_name}
                      </p>
                    )}
                    {booking.checkin_time && (
                      <p className="text-sm text-emerald-600 mt-1">
                        Arrived at {formatTime(booking.checkin_time)}
                        {booking.checkout_time && (
                          <> • Completed ({formatDuration(booking.total_time_on_site_minutes)})</>
                        )}
                      </p>
                    )}
                  </div>

                  {/* Action buttons for staff */}
                  {booking.assigned_staff_id === user?.id && (
                    <div className="flex gap-2 mt-3 md:mt-0">
                      {booking.status === 'assigned' && (
                        <Button
                          onClick={() => handleAccept(booking.id)}
                          disabled={actionLoading === booking.id}
                          className="bg-zinc-900 hover:bg-zinc-800"
                          data-testid={`accept-btn-${booking.id}`}
                        >
                          {actionLoading === booking.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Accept Job'
                          )}
                        </Button>
                      )}
                      {booking.status === 'accepted' && !booking.checkin_time && (
                        <Button
                          onClick={() => handleArrive(booking.id)}
                          disabled={actionLoading === booking.id}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`arrive-btn-${booking.id}`}
                        >
                          {actionLoading === booking.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Play className="mr-2 h-4 w-4" />
                              Arrive
                            </>
                          )}
                        </Button>
                      )}
                      {booking.status === 'in_progress' && (
                        <Button
                          onClick={() => handleDepart(booking.id)}
                          disabled={actionLoading === booking.id}
                          className="bg-amber-600 hover:bg-amber-700"
                          data-testid={`depart-btn-${booking.id}`}
                        >
                          {actionLoading === booking.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Square className="mr-2 h-4 w-4" />
                              Depart
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* View button for managers */}
                  {isManager && (
                    <Link to={`/bookings/${booking.id}`} className="mt-3 md:mt-0 md:ml-4">
                      <Button variant="outline" size="sm" data-testid={`view-booking-${booking.id}`}>
                        View Details
                      </Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions for Managers */}
      {isManager && (
        <div className="grid grid-cols-2 gap-4">
          <Link to="/bookings/new">
            <Card className="border-zinc-100 hover:border-zinc-300 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-zinc-900 flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-medium text-zinc-900">New Booking</h3>
                  <p className="text-sm text-zinc-500">Create a quick booking</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/clients/new">
            <Card className="border-zinc-100 hover:border-zinc-300 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-zinc-900 flex items-center justify-center">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-medium text-zinc-900">New Client</h3>
                  <p className="text-sm text-zinc-500">Add a new client</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
